import Immutable from 'immutable';

import {
	webapi,
	createModuleState,
	createModelForState,
	REPORTED_EXCEPTION,
	StaticLookup,
	setPreference,
	getPreference,
} from 'ps-gui';
import {
	createSearchModel,
	getSqlFulltextFilter,
	parseRoleId
} from './utils';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_STATE = createModuleState('MyRecordings');

export default class MyRecordingsModel {
	#data;

	constructor(data) {
		this.#data = data || EMPTY_MAP;
	}

	#setProperty(name, value) {
		if (Array.isArray(name)) {
			return new MyRecordingsModel(this.#data.setIn(name, value));
		} else {
			return new MyRecordingsModel(this.#data.set(name, value));
		}
	}

	setPartyId(partyId) {
		console.log('setPartyId', partyId);
		return this.#setProperty('partyId', partyId);
	}

	async checkGlobalContextChange(globalContext) {
		if (!globalContext) {
			return this;
		}
		const gcLangId = globalContext.getLanguageId();
		const langId = this.#data.get('langId');
		if (gcLangId === langId) {
			return this;
		}
		return (await this.initialize(globalContext)).#setProperty('languageTimestamp', new Date().getTime());
	}

	getLanguageTimestamp() {
		return this.#data.get('languageTimestamp') || 0;
	}

	async initializeLookups() {
		const partyId = this.#data.get('partyId');
		let pi = await webapi.execute('/api/proc/api_myrecordings/getpartyinfo', {
			partyId
		});
		if (pi === REPORTED_EXCEPTION) {
			return this;
		}
		console.log('pi = ', pi);
		let model = this.#setProperty('roleLookup', StaticLookup(Immutable.fromJS(pi.roles || [])));
		if (pi.producerDistributionPeriods) {
			for (let roleId in pi.producerDistributionPeriods) {
				let items = pi.producerDistributionPeriods[roleId] || [];
				model = model.#setProperty('distributionPeriodLookup.' + roleId, StaticLookup(Immutable.fromJS(items)));
			}
		}

		const roleId = pi.roles?.[0]?.key;
		return model
			.setRoleId(roleId);
	}

	setRoleId(roleId) {
		let model = this.#setProperty('roleId', roleId);
		let dp = this.#data.get('distributionPeriodLookup.' + roleId);
		if (dp) {
			model = model.setDistributionPeriodId(dp.getItems().getIn([0, 'key']));
		}
		return model;
	}

	setDistributionPeriodId(value) {
		return this.#setProperty('distributionPeriodId', value);
	}

	#getPreferenceKeyBase() {
		return '//myrec//';
	}

	#getExtFilterPreferenceKey() {
		return this.#getPreferenceKeyBase() + 'extFilter';
	}

	initializePreferences() {
		let data = this.#data;
		let ef = getPreference(this.#getExtFilterPreferenceKey());
		if (!ef) {
			return this;
		}
		const roleLookup = data.get('roleLookup');
		if (roleLookup) {
			const roleId = ef.get('roleId');
			if (roleId && roleLookup.getText(roleId)) {
				data = data.set('roleId', roleId);
			}
		}
		const distLookup = data.get('distributionPeriodLookup.' + data.get('roleId'));
		if (distLookup) {
			const distPeriodId = ef.get('distributionPeriodId') || distLookup.getItems().getIn([0, 'key']);
			data = data.set('distributionPeriodId', distPeriodId);
		}
		if (ef.get('sources')) {
			data = data.set('sources', ef.get('sources'));
		}
		return new MyRecordingsModel(data);
	}

	async initialize(globalContext) {
		console.log('inicializuji myrecordings');
		let model = await this.initializeLookups();
		model = model.#setProperty('sources', Immutable.Map({
			isrcProtocol: true,
			other: true
		}));
		let srch = createSearchModel('/api/dq/sv_my_recording').setFixedFilter({ creator_id: -1 }).setProcessFilter(processSearchFilter);
		srch = srch.setOutput(getSearchOutputColumns());
		srch = srch.initializePreferences();
		model = model.setSearch(srch);
		model = model.initializePreferences();
		model = await model.refreshSearch(true);
		return model.#setProperty('langId', globalContext.getLanguageId());
	}

	async refreshSearch(dontSavePreference) {
		const roleId = this.#data.get('roleId');
		if (!roleId) {
			return this;
		}
		const partyRole = parseRoleId(roleId) || {};
		const distributionPeriodId = this.#data.get('distributionPeriodId');
		const sources = this.#data.get('sources') || EMPTY_MAP;
		let fixedFilter = {};
		if (!dontSavePreference && roleId && distributionPeriodId) {
			setPreference(this.#getExtFilterPreferenceKey(), Immutable.Map({
				roleId,
				distributionPeriodId,
				sources
			}));
		}
		if (partyRole.roleTypeId === 'PRO') {
			fixedFilter.producer_id = partyRole.partyId;
			fixedFilter.producer_distribution_period_id = distributionPeriodId;
		} else {
			fixedFilter.creator_id = partyRole.partyId;
			fixedFilter.creator_role_type_id = partyRole.roleTypeId;
		}
		let isIsrcProtocol = '#';
		if (sources.get('isrcProtocol')) {
			if (sources.get('other')) {
				isIsrcProtocol = null;
			} else {
				isIsrcProtocol = 'Y';
			}
		} else if (sources.get('other')) {
			isIsrcProtocol = 'N';
		}
		fixedFilter.is_isrc_protocol = isIsrcProtocol;

		let srch = this.getSearch().setFixedFilter(fixedFilter);
		srch = await srch.search(1);
		return this.setSearch(srch);
	}

	async setSource(name, value) {
		let model = this.#setProperty(['sources', name], value);
		return await model.refreshSearch();
	}

	setSearch(model) {
		return new MyRecordingsModel(this.#data.set('search', model.getState()));
	}

	getSearch() {
		return createModelForState(this.#data.get('search'));
	}

	getData() {
		return this.#data;
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}

function processSearchFilter(filter) {
	if (filter.recording) {
		filter.recording = getSqlFulltextFilter(filter.recording);
	}
	return filter;
}

export function getSearchOutputColumns(forExport) {
	if (forExport) {
		return [
			'recording_id',
			'title',
			'subtitle',
			'production_year',
			'release_year',
			'is_isrc_protocol',
			'length',
			'audio_video',
			'isrc'
		];
	} else {
		return [
			'recording',
			'production_release_year',
			'length',
			'audio_video',
			'isrc'
		];
	}
}
